import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: February 2026</p>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              Red Panda Socials ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our property 
              booking platform. Please read this policy carefully to understand our practices regarding your 
              personal data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li><strong>Account Information:</strong> Name, email address, profile picture (via Google Sign-In)</li>
              <li><strong>Profile Information:</strong> Phone number, date of birth, hometown (optional)</li>
              <li><strong>Booking Information:</strong> Property preferences, check-in/check-out dates, guest details</li>
              <li><strong>Payment Information:</strong> Processed securely through Razorpay (we don't store card details)</li>
              <li><strong>Communications:</strong> Messages with hosts, support inquiries, reviews</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
              <li><strong>Usage Data:</strong> Pages visited, search queries, booking history</li>
              <li><strong>Location Data:</strong> IP address, general geographic location</li>
              <li><strong>Cookies:</strong> Session cookies for authentication and preferences</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.3 Information from Third Parties</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li><strong>Google:</strong> Basic profile information when you sign in with Google</li>
              <li><strong>Payment Providers:</strong> Transaction status and confirmation from Razorpay</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-600 leading-relaxed">We use your information to:</p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li>Create and manage your account</li>
              <li>Process bookings and payments</li>
              <li>Facilitate communication between Guests and Hosts</li>
              <li>Send booking confirmations, reminders, and updates</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Improve our Platform and develop new features</li>
              <li>Detect and prevent fraud or unauthorized activities</li>
              <li>Comply with legal obligations</li>
              <li>Send promotional communications (with your consent)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Information Sharing</h2>
            <p className="text-gray-600 leading-relaxed">We may share your information with:</p>
            
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.1 Other Users</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>Hosts can see Guest names, profile photos, and booking details</li>
              <li>Guests can see Host names, profile photos, and property information</li>
              <li>Reviews and ratings are publicly visible</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.2 Service Providers</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li><strong>Razorpay:</strong> Payment processing</li>
              <li><strong>Cloudinary:</strong> Image storage and delivery</li>
              <li><strong>Google:</strong> Authentication services</li>
              <li><strong>MongoDB:</strong> Database hosting</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.3 Legal Requirements</h3>
            <p className="text-gray-600">
              We may disclose information if required by law, court order, or government request, or to protect 
              our rights, property, or safety.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-600 leading-relaxed">
              We implement appropriate security measures to protect your information:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li>SSL/TLS encryption for all data transmissions</li>
              <li>Secure authentication via Google OAuth</li>
              <li>HttpOnly cookies for session management</li>
              <li>Regular security audits and updates</li>
              <li>Limited employee access to personal data</li>
              <li>Secure payment processing through PCI-compliant Razorpay</li>
            </ul>
            <p className="text-gray-600 mt-4">
              While we strive to protect your information, no method of transmission over the internet is 100% 
              secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain your information for as long as necessary to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li>Provide our services and maintain your account</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Resolve disputes and enforce agreements</li>
              <li>Booking records are retained for 7 years for tax purposes</li>
              <li>Account data is deleted within 30 days of account closure</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed">
              You have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Portability:</strong> Receive your data in a structured format</li>
              <li><strong>Objection:</strong> Object to certain processing activities</li>
              <li><strong>Withdraw Consent:</strong> Withdraw marketing consent at any time</li>
            </ul>
            <p className="text-gray-600 mt-4">
              To exercise these rights, contact us at admin@redpandasocials.com
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Cookies Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We use cookies and similar technologies for:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mt-2 space-y-1">
              <li><strong>Essential Cookies:</strong> Required for authentication and security</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Analytics Cookies:</strong> Understand how you use our Platform</li>
            </ul>
            <p className="text-gray-600 mt-4">
              You can control cookies through your browser settings. Disabling essential cookies may affect 
              Platform functionality.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-600 leading-relaxed">
              Our Platform is not intended for users under 18 years of age. We do not knowingly collect 
              personal information from children. If we learn that we have collected information from a 
              child under 18, we will delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. International Data Transfers</h2>
            <p className="text-gray-600 leading-relaxed">
              Your information may be transferred to and processed in countries other than India. We ensure 
              appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant changes 
              by posting the new policy on this page and updating the "Last updated" date. Your continued use 
              of the Platform after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-4 bg-gray-100 p-4 rounded-lg">
              <p className="text-gray-700"><strong>Red Panda Socials</strong></p>
              <p className="text-gray-700"><strong>Data Protection Contact</strong></p>
              <p className="text-gray-600">Email: admin@redpandasocials.com</p>
              <p className="text-gray-600">Phone: +91 8799095561</p>
              <p className="text-gray-600">Address: Surat, Gujarat, India</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Grievance Officer</h2>
            <p className="text-gray-600 leading-relaxed">
              In accordance with the Information Technology Act, 2000 and rules made thereunder, the 
              Grievance Officer for the purpose of this Privacy Policy is:
            </p>
            <div className="mt-4 bg-gray-100 p-4 rounded-lg">
              <p className="text-gray-700"><strong>Grievance Officer</strong></p>
              <p className="text-gray-600">Email: admin@redpandasocials.com</p>
              <p className="text-gray-600">Response Time: Within 30 days of receiving complaint</p>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Privacy;
